import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ImageIcon, Loader2, Trash2 } from "lucide-react";
import type { Category } from "@arc/contracts";
import { IMAGE_UPLOAD_EXTENSIONS } from "@arc/contracts";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../auth/keycloak.js";
import { AssetLibraryDrawer } from "./asset-library/AssetLibraryDrawer.js";

export interface EditCategoryDialogProps {
  category: Category;
  session: AuthSession | null;
  /** `null` clears the icon; a number sets it. Resolves when the write + invalidations have landed. */
  onSave: (imageId: number | null) => Promise<void>;
  onClose: () => void;
}

/** The legacy states 120×120px is optimal (`de_default.js:463`). It only ever SUGGESTS this and accepts anything —
 *  kept advisory here (owner-confirmed), consistent with the Check Manual gate. */

/**
 * "Edit category" (ADR-043 D6) — the icon editor, opened from a category row's ⛰ action.
 *
 * The legacy's equivalent (`ARLMS_FacilityManagement.js:772-846`) is a two-tab window: a **Vorschau** tab rendering a
 * fake manual tile, and a **Einstellungen** tab with the name field, a master-scope file selection and the size hint.
 * The preview is a genuinely good affordance — an author should see the icon where it will actually appear — but a
 * whole tab holding one fake tile is window-furniture, so it collapses into the card-scale preview below. Renaming
 * already lives inline on the row, so this dialog is about the icon only.
 *
 * The picker is the shipped {@link AssetLibraryDrawer} in **pick** mode, Master scope, filtered to
 * `IMAGE_UPLOAD_EXTENSIONS` — the same constant the server validates against, so the drawer cannot offer a file the
 * PATCH would then reject.
 */
export function EditCategoryDialog({ category, session, onSave, onClose }: EditCategoryDialogProps) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState<"set" | "clear" | null>(null);

  const run = (imageId: number | null, kind: "set" | "clear") => {
    setSaving(kind);
    void onSave(imageId).finally(() => {
      setSaving(null);
    });
  };

  return (
    <>
      <Dialog isOpen onOpenChange={(open) => !open && onClose()}>
        <DialogTitle>{t("facilityManagement.categories.editTitle", { name: category.name })}</DialogTitle>
        <DialogDescription>
          {t("facilityManagement.categories.iconHelp")} {t("facilityManagement.categories.optimalIconSize")}
        </DialogDescription>

        {/* Card-scale preview on the CARD surface — never a hardcoded white. Category icons are transparent PNGs, so a
            fixed light backdrop would look right in one theme and wrong in the other. */}
        <div className="border-border bg-muted/40 flex items-center gap-4 rounded-lg border p-4">
          <div className="border-border bg-card grid size-[120px] shrink-0 place-items-center overflow-hidden rounded-md border">
            {category.imageUrl ? (
              <img src={category.imageUrl} alt="" className="size-full object-contain" />
            ) : (
              <ImageIcon aria-hidden="true" className="text-muted-foreground size-8" />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-muted-foreground text-sm">
              {category.imageFileId == null ? t("facilityManagement.categories.noIcon") : t("facilityManagement.categories.currentIcon")}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" isDisabled={saving !== null} onPress={() => setPicking(true)}>
                {category.imageFileId == null ? t("facilityManagement.categories.chooseImage") : t("facilityManagement.categories.replaceImage")}
              </Button>
              {category.imageFileId != null && (
                <Button variant="ghost" size="sm" isDisabled={saving !== null} onPress={() => run(null, "clear")}>
                  {saving === "clear" ? (
                    <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                  ) : (
                    <Trash2 aria-hidden="true" className="size-3.5" />
                  )}
                  {t("facilityManagement.categories.removeIcon")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          {/* Disabled while a save is in flight (diff panel). Closing mid-save unmounts this dialog and with it the
              only concurrency guard (`saving`); reopening would mount a fresh one and permit a SECOND overlapping
              PATCH. The server UPDATE is unconditional, so if the two commit out of order the older icon wins — and
              a category icon is the card image for every manual in every facility it is attached to. */}
          <Button variant="outline" isDisabled={saving !== null} onPress={onClose}>
            {saving !== null ? t("facilityManagement.categories.saving") : t("facilityManagement.categories.done")}
          </Button>
        </DialogFooter>
      </Dialog>

      {picking && (
        <AssetLibraryDrawer
          session={session}
          isOpen
          mode="pick"
          types={[...IMAGE_UPLOAD_EXTENSIONS]}
          onSelect={(file) => {
            setPicking(false);
            run(file.fileId, "set");
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}
